'use client'

import { createContext, useContext, useState, useLayoutEffect } from 'react'

export const SIGNED_OUT_SESSION = {
  isSignedIn: false,
  hasSeekerProfile: false,
  hasReferrerProfile: false,
}

export const SessionContext = createContext(SIGNED_OUT_SESSION)

const NAV_SESSION_CACHE_KEY = 'nav_session_state'

function readSessionCache() {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(NAV_SESSION_CACHE_KEY))
    if (parsed && typeof parsed === 'object') {
      return {
        isSignedIn: Boolean(parsed.isSignedIn),
        hasSeekerProfile: Boolean(parsed.hasSeekerProfile),
        hasReferrerProfile: Boolean(parsed.hasReferrerProfile),
      }
    }
  } catch {
    // ignore malformed cache
  }
  return null
}

export default function SessionProvider({ children }) {
  const [session, setSession] = useState(null)

  useLayoutEffect(() => {
    if (!/(?:^|;\s*)sb-[^;=]+-auth-token=/.test(document.cookie)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- pre-paint sync from external system (cookie)
      setSession(SIGNED_OUT_SESSION)
      return
    }

    setSession(readSessionCache() ?? { ...SIGNED_OUT_SESSION, isSignedIn: true })

    let cancelled = false
    fetch('/api/nav-session', { credentials: 'same-origin' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return
        const next = {
          isSignedIn: Boolean(data.user),
          hasSeekerProfile: Boolean(data.hasSeekerProfile),
          hasReferrerProfile: Boolean(data.hasReferrerProfile),
        }
        setSession(next)
        try {
          window.sessionStorage.setItem(NAV_SESSION_CACHE_KEY, JSON.stringify(next))
        } catch {
          // ignore storage failures
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession() {
  return useContext(SessionContext)
}
